// src/components/ModalCobroMultaAdministrativa.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaTimes, FaExclamationTriangle, FaCalculator, FaUser, FaDollarSign, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp, updateDoc, writeBatch, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './ModalCobroMultaAdministrativa.css';

const ModalCobroMultaAdministrativa = ({ onClose, onConfirm }) => {
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd;
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const errorMessageRef = useRef(null);
    
    // Estados del formulario
    const [selectedCasa, setSelectedCasa] = useState(null);
    const [casas, setCasas] = useState([]);
    const [loadingCasas, setLoadingCasas] = useState(true);
    const [searchCasa, setSearchCasa] = useState('');
    
    // Estados de multas
    const [multasList, setMultasList] = useState([]);
    const [selectedMulta, setSelectedMulta] = useState(null);
    const [fechaFalta, setFechaFalta] = useState(new Date().toISOString().split('T')[0]);
    const [comentarios, setComentarios] = useState('');
    const [enviarNotificacion, setEnviarNotificacion] = useState(true);
    
    // Estados de información de la casa
    const [casaInfo, setCasaInfo] = useState(null);
    const [loadingCasaInfo, setLoadingCasaInfo] = useState(false);
    
    // Estados de configuración
    const [receiptConfig, setReceiptConfig] = useState({});
    const [loadingConfig, setLoadingConfig] = useState(true);

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, []);

    // Cargar casas
    useEffect(() => {
        const loadCasas = async () => {
            if (!fraccionamientoId) {
                setLoadingCasas(false);
                return;
            }
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const snapshot = await getDocs(casasRef);
                const casasList = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(casa => {
                        const rolCasa = (casa.rol || casa.rolAsignado || '').toLowerCase();
                        const perfilCasa = (casa.perfil || '').toLowerCase();
                        const esCaseta = casa.esRolCaseta === true || rolCasa === 'caseta' || perfilCasa === 'caseta';
                        return !esCaseta; // Excluir caseta
                    })
                    .sort((a, b) => {
                        const numA = (a.numero || '').toString();
                        const numB = (b.numero || '').toString();
                        return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
                    });
                setCasas(casasList);
            } catch (error) {
                console.error('Error cargando casas:', error);
                setErrorMessage('Error al cargar las casas');
            } finally {
                setLoadingCasas(false);
            }
        };
        loadCasas();
    }, [fraccionamientoId]);

    // Cargar configuración financiera y multas
    useEffect(() => {
        const loadConfig = async () => {
            if (!fraccionamientoId) {
                setLoadingConfig(false);
                return;
            }
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(fraccRef);
                
                if (snap.exists()) {
                    const data = snap.data();
                    const cfg = data.configuracionRecibos || {};
                    setReceiptConfig(cfg);
                    
                    const configFinanciera = data.configuracionFinanciera || {};
                    const multas = Array.isArray(configFinanciera.multasAdministrativasList)
                        ? configFinanciera.multasAdministrativasList
                        : Array.isArray(configFinanciera.multasList)
                            ? configFinanciera.multasList.map((m, i) => ({ 
                                id: m.id || i, 
                                descripcion: m.descripcion || '', 
                                monto: Number(m.monto || 0) 
                            }))
                            : [];
                    
                    const cleanedMultas = multas
                        .filter((m) => m && (m.descripcion || '').trim() && Number(m.monto || 0) > 0)
                        .map((m, i) => ({ 
                            id: m.id ?? i, 
                            descripcion: m.descripcion.trim(), 
                            monto: Number(m.monto || 0) 
                        }));
                    
                    setMultasList(cleanedMultas);
                }
            } catch (error) {
                console.error('Error cargando configuración:', error);
                setErrorMessage('Error al cargar la configuración');
            } finally {
                setLoadingConfig(false);
            }
        };
        loadConfig();
    }, [fraccionamientoId]);

    // Cargar información de la casa seleccionada
    useEffect(() => {
        const loadCasaInfo = async () => {
            if (!selectedCasa || !fraccionamientoId) {
                setCasaInfo(null);
                return;
            }
            try {
                setLoadingCasaInfo(true);
                const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', selectedCasa.id);
                const casaSnap = await getDoc(casaRef);
                
                if (casaSnap.exists()) {
                    const data = casaSnap.data();
                    setCasaInfo({
                        id: casaSnap.id,
                        numero: data.numero || data.numeroCasa || '',
                        nombreResidente: data.nombre_residente || data.nombreResidente || data.residente || '',
                        adeudo: Number(data.adeudo || 0),
                        saldoFavor: Number(data.saldoFavor || 0),
                        adeudoCapital: Number(data.adeudoCapital || 0),
                        adeudoIntereses: Number(data.adeudoIntereses || 0),
                    });
                }
            } catch (error) {
                console.error('Error cargando información de casa:', error);
                setErrorMessage('Error al cargar información de la casa');
            } finally {
                setLoadingCasaInfo(false);
            }
        };
        loadCasaInfo();
    }, [selectedCasa, fraccionamientoId]);

    // Filtrar casas por búsqueda
    const filteredCasas = useMemo(() => {
        if (!searchCasa.trim()) return casas;
        const search = searchCasa.toLowerCase();
        return casas.filter(casa => {
            const numero = (casa.numero || '').toString().toLowerCase();
            const nombre = (casa.nombre_residente || casa.nombreResidente || casa.residente || '').toLowerCase();
            return numero.includes(search) || nombre.includes(search);
        });
    }, [casas, searchCasa]);

    // Calcular resultado final
    const calculadora = useMemo(() => {
        if (!selectedMulta || !casaInfo) {
            return {
                montoMulta: 0,
                saldoFavorActual: 0,
                adeudoActual: 0,
                saldoFavorDespues: 0,
                adeudoDespues: 0,
                diferencia: 0,
            };
        }

        const montoMulta = Number(selectedMulta.monto || 0);
        const saldoFavorActual = Number(casaInfo.saldoFavor || 0);
        const adeudoActual = Number(casaInfo.adeudo || 0);

        let saldoFavorDespues = saldoFavorActual;
        let adeudoDespues = adeudoActual;
        let diferencia = 0;

        if (saldoFavorActual > 0) {
            // Si tiene saldo a favor, descontar de ahí
            if (saldoFavorActual >= montoMulta) {
                // El saldo a favor cubre completamente la multa
                saldoFavorDespues = saldoFavorActual - montoMulta;
                adeudoDespues = adeudoActual;
            } else {
                // El saldo a favor no cubre completamente, la diferencia va a adeudo
                diferencia = montoMulta - saldoFavorActual;
                saldoFavorDespues = 0;
                adeudoDespues = adeudoActual + diferencia;
            }
        } else {
            // No tiene saldo a favor, se suma al adeudo
            adeudoDespues = adeudoActual + montoMulta;
        }

        return {
            montoMulta,
            saldoFavorActual,
            adeudoActual,
            saldoFavorDespues,
            adeudoDespues,
            diferencia: diferencia || montoMulta,
        };
    }, [selectedMulta, casaInfo]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    // Scroll al mensaje de error cuando aparece
    useEffect(() => {
        if (errorMessage && errorMessageRef.current) {
            errorMessageRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
            });
        }
    }, [errorMessage]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!selectedCasa) {
            setErrorMessage('Selecciona una casa');
            return;
        }

        if (!selectedMulta) {
            setErrorMessage('Selecciona una sanción administrativa');
            return;
        }

        if (!fechaFalta) {
            setErrorMessage('Ingresa la fecha de la falta administrativa');
            return;
        }

        setIsSubmitting(true);

        try {
            const batch = writeBatch(db);
            const montoMulta = Number(selectedMulta.monto || 0);
            const saldoFavorActual = Number(casaInfo.saldoFavor || 0);
            const adeudoActual = Number(casaInfo.adeudo || 0);

            // Calcular nuevos valores
            let nuevoSaldoFavor = saldoFavorActual;
            let nuevoAdeudo = adeudoActual;

            if (saldoFavorActual > 0) {
                if (saldoFavorActual >= montoMulta) {
                    nuevoSaldoFavor = saldoFavorActual - montoMulta;
                } else {
                    const diferencia = montoMulta - saldoFavorActual;
                    nuevoSaldoFavor = 0;
                    nuevoAdeudo = adeudoActual + diferencia;
                }
            } else {
                nuevoAdeudo = adeudoActual + montoMulta;
            }

            // Actualizar casa
            const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', selectedCasa.id);
            batch.update(casaRef, {
                saldoFavor: nuevoSaldoFavor,
                adeudo: nuevoAdeudo,
                saldoActualizadoEn: serverTimestamp(),
            });

            // Crear movimiento en la colección movimientos de la casa
            const movimientosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', selectedCasa.id, 'movimientos');
            const movimientoRef = doc(movimientosRef);
            batch.set(movimientoRef, {
                fraccionamientoId,
                casaId: selectedCasa.id,
                titulo: `Multa Administrativa: ${selectedMulta.descripcion}`,
                descripcion: comentarios || `Multa administrativa aplicada: ${selectedMulta.descripcion}. Fecha de falta: ${fechaFalta}`,
                categoria: 'multa_administrativa',
                tipoMovimiento: 'cargo',
                origen: 'multa-administrativa',
                fecha: serverTimestamp(),
                fechaFalta: new Date(fechaFalta),
                monto: montoMulta,
                multaId: selectedMulta.id,
                multaDescripcion: selectedMulta.descripcion,
                actorUid: userData?.uid || null,
                actorNombre: userData?.nombre || userData?.nombreCompleto || 'Administrador',
            });

            // Guardar en historial de multas administrativas
            const historialRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
            const historialDocRef = doc(historialRef);
            batch.set(historialDocRef, {
                casaId: selectedCasa.id,
                casaNumero: casaInfo.numero,
                nombreResidente: casaInfo.nombreResidente,
                multaId: selectedMulta.id,
                multaDescripcion: selectedMulta.descripcion,
                monto: montoMulta,
                fechaFalta: new Date(fechaFalta),
                fechaCaptura: serverTimestamp(),
                comentarios: comentarios || '',
                actorUid: userData?.uid || null,
                actorNombre: userData?.nombre || userData?.nombreCompleto || 'Administrador',
                saldoFavorAntes: saldoFavorActual,
                adeudoAntes: adeudoActual,
                saldoFavorDespues: nuevoSaldoFavor,
                adeudoDespues: nuevoAdeudo,
            });

            // Generar recibo (guardado en historial, no enviado)
            const folioSerie = (receiptConfig.folioSerie || 'R').replace(/[^A-Za-z]/g, '') || 'R';
            const folioConsecutivo = receiptConfig.folioConsecutivo || 1;
            const folio = `${folioSerie}${String(folioConsecutivo).padStart(6, '0')}`;

            const reciboData = {
                casaId: selectedCasa.id,
                casaNumero: casaInfo.numero,
                nombreResidente: casaInfo.nombreResidente,
                concepto: `Multa Administrativa: ${selectedMulta.descripcion}`,
                monto: montoMulta,
                montoNeto: montoMulta,
                fecha: serverTimestamp(),
                fechaFalta: new Date(fechaFalta),
                metodoPago: 'Multa Administrativa',
                referencia: `MULTA-${selectedMulta.id}`,
                observaciones: comentarios || '',
                folio,
                folioSerie,
                folioConsecutivo,
                tipo: 'multa_administrativa',
                multaId: selectedMulta.id,
                multaDescripcion: selectedMulta.descripcion,
                enviado: false, // No se envía, solo se almacena
            };

            const recibosHistorialRef = collection(db, 'fraccionamientos', fraccionamientoId, 'recibosHistorial');
            const reciboDocRef = doc(recibosHistorialRef);
            batch.set(reciboDocRef, reciboData);

            await batch.commit();

            // Enviar notificación si está habilitada
            if (enviarNotificacion) {
                try {
                    // Buscar usuarios asociados a la casa en la colección global de usuarios
                    const usuariosRef = collection(db, 'usuarios');
                    
                    // Intentar buscar por casaId primero
                    let usuariosQuery = query(
                        usuariosRef,
                        where('fraccionamientoId', '==', fraccionamientoId),
                        where('casaId', '==', selectedCasa.id)
                    );
                    let usuariosSnap = await getDocs(usuariosQuery);
                    
                    // Si no se encuentran por casaId, intentar por numeroCasa
                    if (usuariosSnap.empty && casaInfo.numero) {
                        usuariosQuery = query(
                            usuariosRef,
                            where('fraccionamientoId', '==', fraccionamientoId),
                            where('numeroCasa', '==', String(casaInfo.numero))
                        );
                        usuariosSnap = await getDocs(usuariosQuery);
                    }
                    
                    // También intentar buscar por userId si la casa tiene ese campo
                    if (usuariosSnap.empty && selectedCasa.userId) {
                        try {
                            // Intentar buscar directamente por el ID del documento (userId)
                            const usuarioDirectoRef = doc(db, 'usuarios', selectedCasa.userId);
                            const usuarioDirectoSnap = await getDoc(usuarioDirectoRef);
                            if (usuarioDirectoSnap.exists()) {
                                // Crear un snapshot simulado para mantener la consistencia
                                usuariosSnap = { 
                                    empty: false, 
                                    size: 1, 
                                    docs: [usuarioDirectoSnap],
                                    forEach: (callback) => callback(usuarioDirectoSnap)
                                };
                                console.log(`✅ [Multas] Usuario encontrado directamente por userId: ${selectedCasa.userId}`);
                            }
                        } catch (error) {
                            console.warn('⚠️ [Multas] Error buscando usuario por userId directo:', error);
                        }
                        
                        // Si aún no se encuentra, intentar por uid en query
                        if (usuariosSnap.empty) {
                            usuariosQuery = query(
                                usuariosRef,
                                where('fraccionamientoId', '==', fraccionamientoId),
                                where('uid', '==', selectedCasa.userId)
                            );
                            usuariosSnap = await getDocs(usuariosQuery);
                        }
                    }
                    
                    // Si aún no se encuentra, intentar buscar por userId de casaInfo
                    if (usuariosSnap.empty && casaInfo?.userId) {
                        try {
                            const usuarioDirectoRef = doc(db, 'usuarios', casaInfo.userId);
                            const usuarioDirectoSnap = await getDoc(usuarioDirectoRef);
                            if (usuarioDirectoSnap.exists()) {
                                usuariosSnap = { 
                                    empty: false, 
                                    size: 1, 
                                    docs: [usuarioDirectoSnap],
                                    forEach: (callback) => callback(usuarioDirectoSnap)
                                };
                                console.log(`✅ [Multas] Usuario encontrado por userId de casaInfo: ${casaInfo.userId}`);
                            }
                        } catch (error) {
                            console.warn('⚠️ [Multas] Error buscando usuario por userId de casaInfo:', error);
                        }
                    }
                    
                    // Crear notificaciones para cada usuario encontrado
                    const notificacionesPromises = [];
                    console.log(`🔔 [Multas] Buscando usuarios para notificar. Encontrados: ${usuariosSnap.size}`);
                    
                    usuariosSnap.forEach((usuarioDoc) => {
                        const userId = usuarioDoc.id;
                        const usuarioData = usuarioDoc.data();
                        console.log(`🔔 [Multas] Creando notificación para usuario: ${userId} (${usuarioData.nombre || usuarioData.nombreUsuario || 'Sin nombre'})`);
                        
                        const notificacionRef = collection(db, 'usuarios', userId, 'notificaciones');
                        
                        const notificacionData = {
                            fraccionamientoId,
                            titulo: `Sanción Administrativa: ${selectedMulta.descripcion}`,
                            descripcion: `Se ha aplicado una sanción administrativa de ${formatCurrency(montoMulta)}${comentarios ? '. ' + comentarios : ''}. Fecha de falta: ${new Date(fechaFalta).toLocaleDateString('es-MX')}`,
                            tipo: 'multa_administrativa',
                            categoria: 'finanzas',
                            segmento: 'financiera',
                            multaId: historialDocRef.id,
                            reciboId: reciboDocRef.id,
                            casaId: selectedCasa.id,
                            casaNumero: casaInfo.numero,
                            monto: montoMulta,
                            fecha: serverTimestamp(),
                            leida: false,
                            importancia: 'alta',
                        };
                        
                        console.log(`🔔 [Multas] Datos de notificación:`, {
                            userId,
                            tipo: notificacionData.tipo,
                            titulo: notificacionData.titulo,
                            monto: notificacionData.monto,
                            leida: notificacionData.leida
                        });
                        
                        notificacionesPromises.push(
                            addDoc(notificacionRef, notificacionData).then((docRef) => {
                                console.log(`✅ [Multas] Notificación creada con ID: ${docRef.id} para usuario: ${userId}`);
                                return docRef;
                            }).catch((error) => {
                                console.error(`❌ [Multas] Error creando notificación para usuario ${userId}:`, error);
                                throw error;
                            })
                        );
                    });
                    
                    if (notificacionesPromises.length > 0) {
                        await Promise.all(notificacionesPromises);
                        console.log(`✅ [Multas] Notificaciones enviadas exitosamente a ${notificacionesPromises.length} usuario(s)`);
                    } else {
                        console.warn('⚠️ [Multas] No se encontraron usuarios para notificar. Intentando buscar por userId de la casa...');
                        // Intentar buscar usuario directamente por userId si la casa lo tiene
                        if (selectedCasa.userId) {
                            try {
                                const usuarioDirectoRef = doc(db, 'usuarios', selectedCasa.userId);
                                const usuarioDirectoSnap = await getDoc(usuarioDirectoRef);
                                if (usuarioDirectoSnap.exists()) {
                                    const notificacionRef = collection(db, 'usuarios', selectedCasa.userId, 'notificaciones');
                                    const notificacionData = {
                                        fraccionamientoId,
                                        titulo: `Sanción Administrativa: ${selectedMulta.descripcion}`,
                                        descripcion: `Se ha aplicado una sanción administrativa de ${formatCurrency(montoMulta)}${comentarios ? '. ' + comentarios : ''}. Fecha de falta: ${new Date(fechaFalta).toLocaleDateString('es-MX')}`,
                                        tipo: 'multa_administrativa',
                                        categoria: 'finanzas',
                                        segmento: 'financiera',
                                        multaId: historialDocRef.id,
                                        reciboId: reciboDocRef.id,
                                        casaId: selectedCasa.id,
                                        casaNumero: casaInfo.numero,
                                        monto: montoMulta,
                                        fecha: serverTimestamp(),
                                        leida: false,
                                        importancia: 'alta',
                                    };
                                    const docRef = await addDoc(notificacionRef, notificacionData);
                                    console.log(`✅ [Multas] Notificación creada directamente para userId: ${selectedCasa.userId}, ID: ${docRef.id}`);
                                }
                            } catch (error) {
                                console.error('❌ [Multas] Error creando notificación directa:', error);
                            }
                        }
                    }
                } catch (notifError) {
                    console.error('❌ [Multas] Error enviando notificación:', notifError);
                    // No fallar el proceso si la notificación falla
                }
            }

            if (onConfirm) {
                onConfirm({
                    casa: selectedCasa,
                    multa: selectedMulta,
                    monto: montoMulta,
                    fechaFalta,
                    comentarios,
                });
            }

            onClose();
        } catch (error) {
            console.error('Error aplicando sanción:', error);
            setErrorMessage(`Error al aplicar la sanción: ${error.message || 'Error desconocido'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-cobro-multa-backdrop" onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        }}>
            <div className="modal-cobro-multa-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-cobro-multa-header">
                    <h2>
                        <FaDollarSign style={{ marginRight: '8px' }} />
                        Cobro de Sanción Administrativa
                    </h2>
                    <button type="button" className="modal-close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-cobro-multa-form">
                    {errorMessage && (
                        <div 
                            ref={errorMessageRef}
                            className="modal-error-message modal-error-message-sticky"
                        >
                            <FaExclamationTriangle /> {errorMessage}
                        </div>
                    )}

                    <div className="modal-form-layout">
                        {/* Columna izquierda: Campos del formulario */}
                        <div className="modal-form-left">
                            {/* Selección de Casa */}
                            <div className="form-section">
                                <label className="form-label">
                                    <FaUser style={{ marginRight: '8px' }} />
                                    Seleccionar Residente *
                                </label>
                                <div className="casa-search-wrapper">
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Buscar por número de casa o nombre..."
                                        value={searchCasa}
                                        onChange={(e) => {
                                            setSearchCasa(e.target.value);
                                            // Limpiar selección si se borra el texto
                                            if (!e.target.value.trim()) {
                                                setSelectedCasa(null);
                                            }
                                        }}
                                        onFocus={() => {
                                            // Mantener la búsqueda visible al hacer focus
                                        }}
                                    />
                                    {/* Lista de resultados de búsqueda */}
                                    {searchCasa.trim() && filteredCasas.length > 0 && !selectedCasa && (
                                        <div className="casas-search-results">
                                            {filteredCasas.slice(0, 8).map((casa) => (
                                                <div
                                                    key={casa.id}
                                                    className="casa-search-result-item"
                                                    onClick={() => {
                                                        setSelectedCasa(casa);
                                                        setSearchCasa(casa.numero || casa.numeroCasa || '');
                                                    }}
                                                >
                                                    <div className="casa-search-result-info">
                                                        <strong>{casa.numero || casa.numeroCasa || 'Sin número'}</strong>
                                                        <span>{casa.nombre_residente || casa.nombreResidente || casa.residente || 'Sin nombre'}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {selectedCasa && (
                                    <div className="selected-casa-display">
                                        <strong>{selectedCasa.numero || selectedCasa.numeroCasa || 'Sin número'}</strong>
                                        <span>{selectedCasa.nombre_residente || selectedCasa.nombreResidente || selectedCasa.residente || 'Sin nombre'}</span>
                                        <button
                                            type="button"
                                            className="clear-selection-btn"
                                            onClick={() => {
                                                setSelectedCasa(null);
                                                setSearchCasa('');
                                            }}
                                            title="Limpiar selección"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Información de la Casa Seleccionada */}
                            {selectedCasa && casaInfo && (
                                <div className="casa-info-card">
                                    <h3>Información del Residente</h3>
                                    <div className="casa-info-grid">
                                        <div className="info-item">
                                            <span className="info-label">Casa:</span>
                                            <span className="info-value">{casaInfo.numero}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Residente:</span>
                                            <span className="info-value">{casaInfo.nombreResidente}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Saldo a Favor:</span>
                                            <span className={`info-value ${casaInfo.saldoFavor > 0 ? 'positive' : ''}`}>
                                                {formatCurrency(casaInfo.saldoFavor)}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Adeudo Actual:</span>
                                            <span className={`info-value ${casaInfo.adeudo > 0 ? 'negative' : ''}`}>
                                                {formatCurrency(casaInfo.adeudo)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Selección de Multa */}
                            <div className="form-section">
                                <label className="form-label">
                                    <FaDollarSign style={{ marginRight: '8px' }} />
                                    Sanción Administrativa *
                                </label>
                                {loadingConfig ? (
                                    <p className="loading-text">Cargando sanciones...</p>
                                ) : multasList.length === 0 ? (
                                    <p className="error-text">No hay sanciones administrativas configuradas</p>
                                ) : (
                                    <select
                                        className="form-select"
                                        value={selectedMulta?.id || ''}
                                        onChange={(e) => {
                                            const multa = multasList.find(m => m.id.toString() === e.target.value);
                                            setSelectedMulta(multa || null);
                                        }}
                                        required
                                    >
                                        <option value="">Selecciona una sanción...</option>
                                        {multasList.map((multa) => (
                                            <option key={multa.id} value={multa.id}>
                                                {multa.descripcion} - {formatCurrency(multa.monto)}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Fecha de Falta */}
                            <div className="form-section">
                                <label className="form-label">
                                    Fecha de la Falta Administrativa *
                                </label>
                                <input
                                    type="date"
                                    className="form-input"
                                    value={fechaFalta}
                                    onChange={(e) => setFechaFalta(e.target.value)}
                                    required
                                />
                            </div>

                            {/* Comentarios */}
                            <div className="form-section">
                                <label className="form-label">
                                    <FaInfoCircle style={{ marginRight: '8px' }} />
                                    Comentarios (opcional)
                                </label>
                                <textarea
                                    className="form-textarea"
                                    rows="3"
                                    value={comentarios}
                                    onChange={(e) => setComentarios(e.target.value)}
                                    placeholder="Agregar comentarios sobre la falta administrativa..."
                                />
                            </div>

                            {/* Enviar Notificación */}
                            <div className="form-section">
                                <label className="form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={enviarNotificacion}
                                        onChange={(e) => setEnviarNotificacion(e.target.checked)}
                                    />
                                    <span>Enviar notificación al residente</span>
                                </label>
                            </div>
                        </div>

                        {/* Columna derecha: Ayuda y Calculadora */}
                        <div className="modal-form-right">
                            {/* Recuadro de Ayuda Rápida */}
                            <div className="ayuda-rapida-section">
                                <h3>
                                    <FaInfoCircle style={{ marginRight: '8px' }} />
                                    Ayuda Rápida
                                </h3>
                                <div className="ayuda-content">
                                    <p><strong>1. Selecciona el residente:</strong> Escribe el número de casa o nombre para buscar.</p>
                                    <p><strong>2. Elige la sanción:</strong> Selecciona la falta administrativa desde el menú desplegable.</p>
                                    <p><strong>3. Fecha de falta:</strong> Indica cuándo ocurrió la falta administrativa.</p>
                                    <p><strong>4. Comentarios:</strong> Agrega detalles adicionales si es necesario.</p>
                                    <p><strong>5. Revisa la calculadora:</strong> Verifica cómo afectará la sanción al saldo del residente.</p>
                                </div>
                            </div>

                            {/* Calculadora */}
                            {selectedMulta && casaInfo && (
                                <div className="calculadora-section">
                                    <h3>
                                        <FaCalculator style={{ marginRight: '8px' }} />
                                        Calculadora de Saldo
                                    </h3>
                                    <div className="calculadora-grid">
                                        <div className="calc-item">
                                            <span className="calc-label">Monto de la Sanción:</span>
                                            <span className="calc-value">{formatCurrency(calculadora.montoMulta)}</span>
                                        </div>
                                        <div className="calc-item">
                                            <span className="calc-label">Saldo a Favor Actual:</span>
                                            <span className={`calc-value ${calculadora.saldoFavorActual > 0 ? 'positive' : ''}`}>
                                                {formatCurrency(calculadora.saldoFavorActual)}
                                            </span>
                                        </div>
                                        <div className="calc-item">
                                            <span className="calc-label">Adeudo Actual:</span>
                                            <span className={`calc-value ${calculadora.adeudoActual > 0 ? 'negative' : ''}`}>
                                                {formatCurrency(calculadora.adeudoActual)}
                                            </span>
                                        </div>
                                        <div className="calc-divider"></div>
                                        <div className="calc-item">
                                            <span className="calc-label">Saldo a Favor Después:</span>
                                            <span className={`calc-value ${calculadora.saldoFavorDespues > 0 ? 'positive' : ''}`}>
                                                {formatCurrency(calculadora.saldoFavorDespues)}
                                            </span>
                                        </div>
                                        <div className="calc-item">
                                            <span className="calc-label">Adeudo Después:</span>
                                            <span className={`calc-value ${calculadora.adeudoDespues > 0 ? 'negative' : ''}`}>
                                                {formatCurrency(calculadora.adeudoDespues)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={isSubmitting}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-primary" disabled={isSubmitting || !selectedCasa || !selectedMulta}>
                            {isSubmitting ? 'Aplicando...' : 'Aplicar Sanción'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ModalCobroMultaAdministrativa;

